package com.siriusxm.pia.views.unifiedaggregator.entities

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.siriusxm.cmc.utils.getString
import com.siriusxm.pia.ApplicationDescriptor
import com.siriusxm.pia.components.buttonRadioGroup
import com.siriusxm.pia.views.mddb.MddbAPI
import com.siriusxm.pia.views.mddb.MddbAugmenter
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.services.cutGuidFrom
import com.siriusxm.pia.rest.unifiedaggregator.entityType
import com.siriusxm.unifiedcontent.entityId
import com.siriusxm.unifiedcontent.entityTypeObj
import contentingestion.unifiedmodel.EntityType
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.Style

/**
 * Defines an entity enhancer plugin, which adds a tab to an entity.
 */
class EntityEnhancerPlugin(
    val aggregatorService: AggregatorService,
    val applicationDescriptor: ApplicationDescriptor,
    val config: JsonObject
) {
    /**
     * The name of the enhancer (to display in the UI)
     */
    val name: String get() = config["name"]?.jsonPrimitive?.contentOrNull ?: applicationDescriptor.name
    private val types = config["types"]?.jsonArray?.flatMap {
        val type = it.jsonPrimitive.contentOrNull
        if (type == "*") {
            aggregatorService.entityTypes.map {
                EntityType.fromName(it.type)
            }
        } else if (type != null) {
            listOf(EntityType.fromName(type))
        } else {
            emptyList()
        }
    }.orEmpty()

    /**
     * Is this plugin available for the given data
     */
    fun enabled(catalogEntity: JsonObject?, atlasEntity: JsonObject?): Boolean {
        val type = catalogEntity?.entityTypeObj ?: atlasEntity?.entityTypeObj ?: return false
        if (!types.contains(type)) return false
        // No views means none of the configured paths can be filled in for this entity (e.g. a
        // non-GUID cut) or none apply to its flags, so there is no tab. A plugin that configures
        // no iframe path at all (MDDB) renders itself.
        if (!hasIframeViews) return true
        return views(catalogEntity, atlasEntity).isNotEmpty()
    }

    private val hasIframeViews: Boolean get() = config["views"] != null || config.getString("path") != null

    /**
     * The iframe views this plugin offers for the entity: the configured `views`, or the single
     * `path`, narrowed to those that apply to the entity's flags and whose placeholders can be filled
     * in (UCA-1208).
     */
    fun views(catalogEntity: JsonObject?, atlasEntity: JsonObject?): List<EnhancerView> =
        resolveEnhancerViews(
            config, catalogEntity?.entityId, atlasEntity?.entityId,
            entityFlags(catalogEntity) + entityFlags(atlasEntity),
            catalogEntity?.entityType ?: atlasEntity?.entityType,
            entityLeagueIndex(catalogEntity) ?: entityLeagueIndex(atlasEntity)
        )

    /**
     * Render the plugin.
     */
    @Composable
    fun render(catalogEntity: JsonObject?, atlasEntity: JsonObject?) {
        if (applicationDescriptor.id == "mddb") {
            // we support this augmenter for backwards compatibility. If we move the MDDB application
            // to its own project, we'll switch and use an iframe.
            val api = MddbAPI(
                aggregatorService.context.configuration.mddbApiUrl,
                aggregatorService.context
            )
            val augmenter = MddbAugmenter(api)
            val type = catalogEntity?.entityTypeObj ?: atlasEntity?.entityTypeObj
            val id = catalogEntity?.entityId
            if (type != null && id != null) {
                augmenter.render(type, id)
            }
        } else {
            Style(EntityEnhancerStyles)
            val views = views(catalogEntity, atlasEntity)
            // Start on the first view again for each entity, so a cut never opens on Segment.
            var selected by remember(catalogEntity?.entityId, atlasEntity?.entityId) { mutableStateOf(0) }
            val view = views.getOrNull(selected) ?: views.firstOrNull() ?: return
            if (views.size > 1) {
                // Only offered when more than one view applies, e.g. a NAVIGABLE cut that is also a
                // segment; a cut with no segment form gets just its one page and no toggle.
                Div({ classes(EntityEnhancerStyles.viewToggle) }) {
                    buttonRadioGroup(selected.toString()) {
                        views.forEachIndexed { index, it -> option(it.label ?: name, index.toString()) }
                        onChange { selected = it.toIntOrNull() ?: 0 }
                    }
                }
            }
            // Keyed on the url so each view gets a fresh iframe. Re-pointing a live iframe at another
            // view's hash left it on about:blank when switching Cut to Segment (UCA-1208).
            key(view.url) {
                Iframe({
                    classes(EntityEnhancerStyles.frame)
                    this.attr("src", view.url)
                }) {}
            }
        }
    }
}

/**
 * StyleSheet for the iframe an entity enhancer tab frames and the toggle above it that switches between
 * its views.
 */
object EntityEnhancerStyles : StyleSheet() {
    /**
     * The framed page. An iframe defaults to 150px tall, which left embedded pages in a tiny scrolling
     * box (UCA-1196), so fill what's left of the viewport below the entity header and tab bar instead.
     */
    val frame by style {
        width(100.percent)
        property("height", "calc(100vh - 20em)")
        minHeight(600.px)
        border(0.px)
        padding(0.px)
        margin(0.px)
    }

    /**
     * The row holding the view toggle (e.g. Cut / Segment), right-aligned above the frame.
     */
    val viewToggle by style {
        textAlign("right")
        margin(0.3.em, 0.px)
    }
}

/**
 * One page an entity enhancer can frame: its toggle [label] (null for a plugin with a single,
 * unlabelled `path`) and the resolved iframe [url].
 */
data class EnhancerView(val label: String?, val url: String)

/**
 * The flags on an entity (`flags` is a JSON array of strings, as read in `EntityList`), empty when the
 * entity is null or has none. An element that isn't a string is skipped rather than thrown on, since
 * this runs while the entity page loads and a malformed flag should only hide a tab.
 */
fun entityFlags(entity: JsonObject?): Set<String> =
    (entity?.get("flags") as? JsonArray)?.mapNotNull { (it as? JsonPrimitive)?.contentOrNull }?.toSet().orEmpty()

/**
 * A league entity's `leagueIndex` (UCA-1213), the legacy numeric league id that mountain-services calls
 * `leagueId`, or null when the entity is null or has no all-digit value.
 */
fun entityLeagueIndex(entity: JsonObject?): String? =
    (entity?.get("leagueIndex") as? JsonPrimitive)?.contentOrNull?.trim()
        ?.takeIf { it.isNotEmpty() && it.all(Char::isDigit) }

/** The all-digit part after the last `:` of a typed league id such as `SL:0`, which is its `leagueIndex`. */
internal fun leagueNumberFrom(typedEntityId: String?): String? =
    typedEntityId?.substringAfterLast(":")?.trim()?.takeIf { it.isNotEmpty() && it.all(Char::isDigit) }

/**
 * The views an enhancer [config] offers for an entity (UCA-1208). A `views` array lists several, each with
 * a `label`, a `path`, an optional `requiresFlag` and an optional `types` list; a plain `path` is one unlabelled view. A view is
 * dropped when it requires a flag the entity lacks or its path can't be filled in (see [enhancerPath]).
 * Order follows the config, so the first view is the default.
 */
fun resolveEnhancerViews(
    config: JsonObject, sourceId: String?, atlasId: String?, flags: Set<String>, entityType: String? = null,
    leagueIndex: String? = null
): List<EnhancerView> {
    val configured = (config["views"] as? JsonArray)?.mapNotNull { it as? JsonObject }
        ?: config.getString("path")?.let { listOf(JsonObject(mapOf("path" to JsonPrimitive(it)))) }
        ?: return emptyList()
    return configured.mapNotNull { view ->
        // A view may name the entity types it applies to (UCA-1213/1214: league and team share the
        // plugin with cut-linear); without `types` it applies to every type the plugin does.
        val viewTypes = (view["types"] as? JsonArray)?.mapNotNull { (it as? JsonPrimitive)?.contentOrNull }
        if (viewTypes != null && entityType !in viewTypes) return@mapNotNull null
        val requiresFlag = view.getString("requiresFlag")
        if (requiresFlag != null && requiresFlag !in flags) return@mapNotNull null
        val url = enhancerPath(view.getString("path") ?: return@mapNotNull null, sourceId, atlasId, leagueIndex)
            ?: return@mapNotNull null
        EnhancerView(view.getString("label"), url)
    }
}

/**
 * Fill the placeholders in an enhancer's configured iframe [configPath]: `{{SOURCE_ID}}`,
 * `{{ATLAS_ID}}`, `{{CUT_GUID}}` (UCA-1196, the GUID in the cut's source id, see [cutGuidFrom]),
 * `{{TEAM_GUID}}` (the GUID in a team's source id, the `teamGuid` Mountain keys teams on),
 * `{{EVENT_GUID}}` (the GUID in an event's source id, Mountain's `liveEventGuid`) and
 * `{{LEAGUE_ID}}` (the league's `leagueIndex`, Mountain's `leagueId`: the entity field, see [entityLeagueIndex], else the number in
 * its typed id such as `SL:0`).
 * Returns null when the path uses one of those and the entity does not have that value, so the tab
 * is hidden rather than framing a page that can find nothing.
 */
fun enhancerPath(
    configPath: String, sourceId: String?, atlasId: String?, leagueIndex: String? = null
): String? {
    var path = configPath
    if (path.contains("{{CUT_GUID}}")) {
        path = path.replace("{{CUT_GUID}}", cutGuidFrom(sourceId) ?: return null)
    }
    if (path.contains("{{TEAM_GUID}}")) {
        path = path.replace("{{TEAM_GUID}}", cutGuidFrom(sourceId) ?: return null)
    }
    if (path.contains("{{EVENT_GUID}}")) {
        path = path.replace("{{EVENT_GUID}}", cutGuidFrom(sourceId) ?: return null)
    }
    if (path.contains("{{LEAGUE_ID}}")) {
        path = path.replace("{{LEAGUE_ID}}", leagueIndex ?: leagueNumberFrom(sourceId) ?: return null)
    }
    return path.replace("{{SOURCE_ID}}", sourceId ?: "")
        .replace("{{ATLAS_ID}}", atlasId ?: "")
}

/**
 * Get the enhancer for this application, null if it's not installed.
 */
fun ApplicationDescriptor.enhancer(app: AggregatorService): EntityEnhancerPlugin? {
    return plugins?.get("entityEnhancer")?.let {
        EntityEnhancerPlugin(app, this, it)
    }
}